
import { A2AMessage, MessageSubscription, MessageBusStats, CommunicationChannel } from '../../types';
import { verifyMessage } from '@/hooks/a2a/useA2AKeys';

class MessageBus {
  private static instance: MessageBus;
  private subscriptions: MessageSubscription[] = [];
  private messageHistory: A2AMessage[] = [];
  private channels: Map<string, CommunicationChannel> = new Map();
  private stats: MessageBusStats = {
    totalMessages: 0,
    messagesByType: {},
    activeSubscribers: 0,
    averageProcessingTime: 0
  };

  private constructor() {
    console.log('A2A Message Bus initialized');
  }

  public static getInstance(): MessageBus {
    if (!MessageBus.instance) {
      MessageBus.instance = new MessageBus();
    }
    return MessageBus.instance;
  }

  public async publish(message: A2AMessage): Promise<void> {
    const fullMessage = {
      ...message,
      signature: message.signature || '',
      keyId: message.keyId || '',
      metadata: {
        protocol: message.metadata?.protocol || 'A2A',
        version: message.metadata?.version || '1.1',
        messageType: message.metadata?.messageType || 'REQUEST',
        ...message.metadata
      }
    };

    // Verify the message signature if it has one
    if (fullMessage.signature && fullMessage.keyId) {
      const bodyToVerify = JSON.stringify({
        id: message.id, 
        timestamp: message.timestamp, 
        fromAgent: message.fromAgent, 
        toAgent: message.toAgent, 
        content: message.content, 
        metadata: message.metadata
      });

      const isValid = await verifyMessage(bodyToVerify, message.signature, message.keyId);
      
      if (!isValid) {
        console.warn('A2A signature invalid, dropping', message.id);
        return;
      }
    }

    this.messageHistory.push(fullMessage);
    this.updateStats(fullMessage);
    this.notifySubscribers(fullMessage);
  }

  private updateStats(message: A2AMessage): void {
    this.stats.totalMessages++;
    const messageType = message.metadata?.messageType || 'UNKNOWN';
    this.stats.messagesByType[messageType] = (this.stats.messagesByType[messageType] || 0) + 1;
  }

  private notifySubscribers(message: A2AMessage): void {
    const startTime = performance.now();
    
    const matchingSubscriptions = this.subscriptions
      .filter(sub => this.isSubscriptionMatching(sub, message))
      .sort((a, b) => (b.priority || 0) - (a.priority || 0));
    
    matchingSubscriptions.forEach(subscription => {
      try {
        subscription.callback(message);
      } catch (error) {
        console.error(`Error in message subscription handler for ${subscription.agentId}:`, error);
      }
    });
    
    this.updateProcessingTime(startTime);
  }

  private isSubscriptionMatching(sub: MessageSubscription, message: A2AMessage): boolean {
    if (sub.agentId !== message.toAgent && sub.agentId !== '*') {
      return false;
    }
    
    if (sub.messageTypes.length > 0 && 
        !sub.messageTypes.includes('*') && 
        !sub.messageTypes.includes(message.metadata?.messageType || 'UNKNOWN')) {
      return false;
    }
    
    return !sub.filter || sub.filter(message);
  }

  private updateProcessingTime(startTime: number): void {
    const processingTime = performance.now() - startTime;
    this.stats.averageProcessingTime = 
      (this.stats.averageProcessingTime * (this.stats.totalMessages - 1) + processingTime) / 
      this.stats.totalMessages;
    
    if (processingTime > 100) {
      console.warn(`High message processing latency: ${processingTime.toFixed(2)}ms`);
    }
  }

  public subscribe(subscription: Omit<MessageSubscription, 'id'>): MessageSubscription {
    const id = `sub_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
    const newSubscription: MessageSubscription = { ...subscription, id };
    
    this.subscriptions.push(newSubscription);
    this.stats.activeSubscribers = this.subscriptions.length;
    
    return newSubscription;
  }

  public unsubscribe(subscriptionId: string): boolean {
    const initialLength = this.subscriptions.length;
    this.subscriptions = this.subscriptions.filter(sub => sub.id !== subscriptionId);
    this.stats.activeSubscribers = this.subscriptions.length;
    
    return initialLength !== this.subscriptions.length;
  }

  public getStats(): MessageBusStats {
    return { ...this.stats };
  }

  public getRecentMessages(agentId: string, limit: number = 10): A2AMessage[] {
    return this.messageHistory
      .filter(msg => msg.toAgent === agentId || msg.fromAgent === agentId)
      .sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime())
      .slice(0, limit);
  }

  public clearAgentSubscriptions(agentId: string): number {
    const initialLength = this.subscriptions.length;
    this.subscriptions = this.subscriptions.filter(sub => sub.agentId !== agentId);
    this.stats.activeSubscribers = this.subscriptions.length;
    
    return initialLength - this.subscriptions.length;
  }

  public createBidirectionalChannel(
    agentIds: string[],
    metadata: any = {}
  ): string {
    if (agentIds.length < 2) {
      throw new Error('At least two agents are required for a bidirectional channel');
    }
    
    const channelId = `channel-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
    
    // Create a channel object
    const channel: CommunicationChannel = {
      id: channelId,
      participants: [...agentIds],
      isActive: true,
      createdAt: new Date(),
      lastActivity: new Date(),
      metadata: metadata
    };
    
    // Store the channel
    this.channels.set(channelId, channel);
    
    // Notify all participants about the channel
    agentIds.forEach(async (agentId) => {
      const otherAgents = agentIds.filter(id => id !== agentId);
      
      const message = {
        fromAgent: 'system',
        toAgent: agentId,
        content: `You've been added to a communication channel with: ${otherAgents.join(', ')}`,
        signature: '',
        keyId: '',
        metadata: {
          protocol: 'A2A',
          version: '1.1',
          messageType: 'COORDINATION',
          channelId,
          participants: agentIds,
          ...metadata
        }
      };
      
      await this.publish(message as A2AMessage);
    });
    
    return channelId;
  }
}

export const messageBus = MessageBus.getInstance();

// Export functions for external use
export function createBidirectionalChannel(agentIds: string[], metadata: any = {}): string {
  return messageBus.createBidirectionalChannel(agentIds, metadata);
}

export { publishMessage } from '../messagePublisher';
